import {
  PlayerImage,
  PlayerName,
  PlayerProfile,
  PlayerSecondaryHeader
} from '@library/components/player';
import type { ConfirmationModalProps } from '@library/components/modals/ConfirmationModal';
import type { GamePlayerProjectionTrading } from '@library/types/trading';
import type { PlayerDescriptors } from '@library/types/player';
import { TradingProjectionControls } from '@bo-components/trading/TradingProjectionControls';
import { TradingProjections } from '@bo-components/trading/projections';
import { useMemo } from 'react';
import { useTradingModalBulkActions } from '@bo-hooks/trading/useTradingModalBulkActions';
import { useTradingPlayerBulkActions } from '@bo-hooks/trading/useTradingPlayerBulkActions';

export interface TradingPlayerProps {
  changes: Array<Partial<GamePlayerProjectionTrading>>;
  durationDisplay?: string;
  gameStartTime: string;
  handleSelectPlayerModal: ({
    gameStartDateTime,
    newPlayerDescriptors,
    newProjection
  }: {
    gameStartDateTime: string;
    newPlayerDescriptors: PlayerDescriptors;
    newProjection: GamePlayerProjectionTrading;
  }) => void;
  handleShowAutoTradeDrawer: ({
    gameStartDateTime,
    newPlayerDescriptors,
    newProjection
  }: {
    gameStartDateTime: string;
    newPlayerDescriptors: PlayerDescriptors;
    newProjection: GamePlayerProjectionTrading;
  }) => void;
  isBulkPlayerInGameEnabled: boolean;
  isBulkPlayerGameModesEqual: boolean;
  leagueId: string;
  playerDescriptors: PlayerDescriptors;
  playerId: string;
  projections: Array<GamePlayerProjectionTrading>;
  setConfirmationModalProps: (
    props: Omit<ConfirmationModalProps, 'onCancelModal'>
  ) => void;
  updateConfirmationModalProp: (
    updateFn: (
      prevProps: Omit<ConfirmationModalProps, 'onCancelModal'>
    ) => Omit<ConfirmationModalProps, 'onCancelModal'>
  ) => void;
  updateProjection: ({
    change,
    initialProjection
  }: {
    change: Partial<GamePlayerProjectionTrading>;
    initialProjection: GamePlayerProjectionTrading;
  }) => void;
}

export const TradingPlayer = ({
  changes,
  durationDisplay = '',
  gameStartTime,
  leagueId,
  handleShowAutoTradeDrawer,
  handleSelectPlayerModal,
  isBulkPlayerInGameEnabled,
  isBulkPlayerGameModesEqual,
  playerId,
  playerDescriptors,
  projections,
  setConfirmationModalProps,
  updateConfirmationModalProp,
  updateProjection
}: TradingPlayerProps) => {
  const [sampleProjection] = projections;

  const { gameId, gameModeIds, position } = sampleProjection || {};

  const { playerName, playerImage } = playerDescriptors;

  const { tradingPlayerBulkActions } = useTradingPlayerBulkActions({
    gameId,
    leagueId,
    playerIds: [playerId]
  });

  const { tradingModalActions } = useTradingModalBulkActions({
    durationDisplay,
    gameModeIds,
    isBulkGameModesEqual: isBulkPlayerGameModesEqual,
    setConfirmationModalProps,
    tradingBulkActions: tradingPlayerBulkActions,
    updateConfirmationModalProp
  });

  const tradingPlayerModalActions = useMemo(() => {
    return {
      handleShowPlayerModal: () => {
        handleSelectPlayerModal({
          gameStartDateTime: gameStartTime,
          newPlayerDescriptors: playerDescriptors,
          newProjection: sampleProjection
        });
      },
      handleShowGameModes: tradingModalActions.handleShowGameModes
    };
  }, [
    tradingModalActions,
    handleSelectPlayerModal,
    gameStartTime,
    playerDescriptors,
    sampleProjection
  ]);

  return (
    <div className="border-soFresh-140 group/player flex h-16 w-full border-b border-r last-of-type:border-b-0">
      <PlayerProfile
        className="flex items-center"
        imageUrl={playerImage}
        name={playerName}
        secondaryHeader={position}
      >
        <div className="px-2">
          <div className="bg-soFresh-120 h-12 w-16 rounded-lg px-2">
            <PlayerImage className="w-14" />
          </div>
        </div>

        <div className="border-soFresh-110 flex w-36 flex-col gap-1 border-r border-solid pl-1 leading-5">
          <PlayerName className="font-medium" />
          <PlayerSecondaryHeader className="text-soClean-160 text-xs font-medium" />
        </div>
      </PlayerProfile>

      <TradingProjections
        changes={changes}
        playerId={playerId}
        projections={projections}
        updateProjection={updateProjection}
      />
      <TradingProjectionControls
        gameId={gameId}
        handleShowAutoTradeDrawer={() =>
          handleShowAutoTradeDrawer({
            gameStartDateTime: gameStartTime,
            newPlayerDescriptors: playerDescriptors,
            newProjection: sampleProjection
          })
        }
        isBulkPlayerInGameEnabled={isBulkPlayerInGameEnabled}
        playerBulkActions={tradingPlayerBulkActions}
        playerModalActions={tradingPlayerModalActions}
      />
    </div>
  );
};
